import { Fragment } from 'react'
import { NextSeo } from 'next-seo'
import { useTranslation } from 'next-i18next'

import NavigationBar from '@/components/NavigationBar'
import Footer from '@/components/Footer'
import { AlternateLink } from '@/components/AlternateLink'

import Form from '@/features/landing/templates/Form'
import FAQs from '@/features/landing/templates/FAQs'
import Blogs from '@/features/landing/templates/Blogs'
import Contact from '@/features/landing/templates/Contact'
import OurTrustingClient from '@/features/landing/templates/OurTrustingClient'

import Hero from '@/features/services/templates/Hero'
import ServiceInfo from '@/features/services/templates/ServiceInfo'
import Section from '@/features/services/templates/Section'
import TextWithImage from '@/features/services/templates/TextWithImage'

import WidgetList from '@/features/services/components/WidgetList'
import IconTextList from '@/features/services/components/IconTextList'
import Pricing from '@/features/services/components/Pricing'
import { HandoutBanner } from '@/features/resources/components/Banner'

import { logEvent } from '@/lib/gtm'

import {
  useMeta,
  useHeroData,
  useServiceInfoData,
  useFAQsData,
  useTargetData,
  useFeatureData,
  useWhatYouWillGetData,
  usePricingData,
} from '@/features/services/constants/compliance'

import { ServicePageProps } from '@/features/services/types'

function CompliancePage({ blogs }: ServicePageProps): React.ReactElement {
  const { t } = useTranslation('services')

  const META = useMeta()
  const HERO = useHeroData()
  const INFO = useServiceInfoData()
  const FAQS = useFAQsData()
  const TARGET = useTargetData()
  const FEATURES = useFeatureData()
  const WHAT_YOU_WILL_GET = useWhatYouWillGetData()
  const PRICING = usePricingData()

  return (
    <Fragment>
      <AlternateLink />
      <NextSeo
        title={META.title}
        description={META.description}
        openGraph={{
          title: META.og.title,
          description: META.og.description,
          images: [
            {
              url: META.og.image.url,
              width: 1200,
              height: 630,
              alt: META.og.title,
            },
          ],
        }}
      />
      <NavigationBar />
      <Hero {...HERO} />
      <ServiceInfo {...INFO} />
      <Section title={t('compliance.features.title')}>
        <IconTextList items={FEATURES} />
      </Section>
      <Section title={t('compliance.whatYouWillGet.title')}>
        <WidgetList items={WHAT_YOU_WILL_GET} />
      </Section>
      <OurTrustingClient />
      <Section>
        <Pricing>{PRICING}</Pricing>
      </Section>
      <TextWithImage {...TARGET} />
      <HandoutBanner
        onClickDownload={() => {
          logEvent({
            ga: {
              category: 'Compliance Audit',
              action: 'Click download handout',
              label: 'handout section',
            },
          })
        }}
      />
      <Form gaLabel={'Compliance Audit'} />
      <FAQs items={FAQS} />
      {blogs && <Blogs blogs={blogs} />}
      <Contact />
      <Footer />
    </Fragment>
  )
}

export default CompliancePage
